// Очередь готовых хотсит-партий: копится в localStorage и отсылается на сервер (POST api/games) в фоне, когда есть
// сеть — при загрузке, при появлении сети, после окончания партии. Чтобы мы могли прочитать и разобрать партии
// (в том числе — что делали боты). Онлайн-партии записывает сам сервер.
import type { Game, GameRecord } from '../types.ts';
import { gameRecord } from '../engine/record.ts';

const QUEUE_KEY = 'krutagidon-outbox-v1';
const CLIENT_KEY = 'krutagidon-client-id';
const SENT_KEY = 'krutagidon-outbox-sent';
/** записей в очереди — не больше; старые вытесняются */
const MAX_QUEUE = 40;
/** сколько последних отправленных сидов помним, чтобы не отослать партию дважды (после перезагрузки на экране итога) */
const MAX_SENT = 100;

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeJson(key: string, v: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* переполнение/приватный режим — очередь необязательна */
  }
}

/** Постоянный идентификатор этого браузера (случайный, без личных данных) */
export function clientId(): string {
  let id = readJson<string | null>(CLIENT_KEY, null);
  if (typeof id !== 'string' || !id) {
    id = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    writeJson(CLIENT_KEY, id);
  }
  return id;
}

const KEY_KEY = 'krutagidon-device-key';

/** Секретный ключ этого браузера: id клиента видят все (приглашения), а говорить за аккаунт — имя, связывание
 *  устройств, зачёт партий в рейтинг — можно только с ключом. Сервер хранит лишь его хеш */
export function deviceKey(): string {
  let k = readJson<string | null>(KEY_KEY, null);
  if (typeof k !== 'string' || k.length < 16) {
    const b = new Uint8Array(18);
    crypto.getRandomValues(b);
    k = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    writeJson(KEY_KEY, k);
  }
  return k;
}

function queue(): GameRecord[] {
  const q = readJson<unknown>(QUEUE_KEY, []);
  return Array.isArray(q) ? (q as GameRecord[]) : [];
}
function sentSeeds(): number[] {
  const s = readJson<unknown>(SENT_KEY, []);
  return Array.isArray(s) ? (s as number[]) : [];
}

/** Поставить готовую партию в очередь (если такая, по сиду, ещё не записана) и попробовать отослать */
export function enqueueGame(G: Game): boolean {
  if (G.phase !== 'ended' || G.seed == null) return false;
  const seed = G.seed;
  if (sentSeeds().includes(seed) || queue().some((r) => r.seed === seed)) return false;
  const rec = gameRecord(G, 'hotseat', clientId(), typeof navigator === 'undefined' ? undefined : navigator.userAgent);
  const q = queue();
  q.push(rec);
  while (q.length > MAX_QUEUE) q.shift();
  writeJson(QUEUE_KEY, q);
  void flushOutbox();
  return true;
}

export function outboxSize(): number {
  return queue().length;
}

let flushing: Promise<void> | null = null;

/** Отослать очередь по одной записи; сетевая ошибка — остановиться до следующего раза, отказ сервера (4xx) — выбросить запись */
export function flushOutbox(): Promise<void> {
  if (flushing) return flushing;
  // сброс признака — в finally-обработчике промиса (микрозадача), а не внутри функции: иначе при синхронном
  // раннем выходе (нет сети) он сбросился бы раньше присваивания и очередь навсегда считала бы себя занятой
  const p = doFlush().finally(() => { if (flushing === p) flushing = null; });
  flushing = p;
  return p;
}

async function doFlush(): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  for (;;) {
    const q = queue();
    const rec = q[0];
    if (!rec) return;
    let status = 0;
    try {
      // ключ устройства — только если запись этого браузера (очередь могла пережить смену id): иначе не в рейтинг
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (rec.client === clientId()) headers['X-Device-Key'] = deviceKey();
      const r = await fetch('api/games', { method: 'POST', headers, body: JSON.stringify(rec) });
      status = r.status;
    } catch {
      return; // нет сети или сервера — попробуем позже
    }
    if (status >= 200 && status < 300) {
      const sent = sentSeeds();
      sent.push(rec.seed);
      while (sent.length > MAX_SENT) sent.shift();
      writeJson(SENT_KEY, sent);
    } else if (status < 400 || status >= 500) return; // сервер занят/ошибка — позже
    // отправлено или отвергнуто — из очереди долой
    writeJson(QUEUE_KEY, queue().filter((x) => x.seed !== rec.seed));
  }
}

/** Фоновая отправка: при загрузке, при появлении сети, при возврате на вкладку */
export function initOutbox(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => void flushOutbox());
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void flushOutbox(); });
  void flushOutbox();
}
